import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";

import { cn } from "@/lib/utils";

import { Button } from "./Button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

export interface SearchableSelectOption {
  value: string;
  label: string;
  id: string;
}

interface SearchableSelectProps {
  value: string;
  onValueChange: (value: string, id?: string) => void;
  options: SearchableSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
  buttonClassName?: string;
}

export function SearchableSelect({
  value,
  onValueChange,
  options,
  placeholder = "Select an option...",
  disabled = false,
  isLoading = false,
  buttonClassName = "",
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);

  // Memoize options map for efficient filter lookups
  const optionsMap = useMemo(() => {
    const map = new Map<string, SearchableSelectOption>();
    options.forEach((opt) => map.set(opt.id, opt));
    return map;
  }, [options]);

  // Find selected option by id, or by value if id doesn't match (for backward compat during init)
  let selectedOption = options.find((option) => option.id === value);
  if (!selectedOption && value) {
    // Try to match by value (e.g., initial load with player name instead of id)
    selectedOption = options.find((option) => option.value === value);
  }

  // Normalize value to id for consistency
  const normalizedValue = selectedOption?.id || value;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || isLoading}
          className={cn(
            "w-full justify-between bg-white rounded-sm border-border-input",
            buttonClassName,
          )}
        >
          <span className="truncate">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          {isLoading ? (
            <Loader2 className="ml-2 h-4 w-4 shrink-0 animate-spin" />
          ) : (
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 bg-white">
        <Command
          filter={(value: string, search: string) => {
            // Use memoized map for O(1) lookup instead of array search
            const option = optionsMap.get(value);
            if (!option) return 0;
            return option.label.toLowerCase().includes(search.toLowerCase())
              ? 1
              : 0;
          }}
        >
          <CommandInput placeholder="Search" />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = option.id === normalizedValue;
                return (
                  <CommandItem
                    key={option.id}
                    value={option.id}
                    onSelect={() => {
                      onValueChange(option.id, option.id);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        isSelected ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {option.label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
